import React from 'react';
import { Helmet } from 'react-helmet-async';

const Support = () => {
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const pageWrapperStyle = {
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px',
    paddingBottom: '40px',
    borderBottom: '3px solid #52A7B0',
    animation: 'fadeIn 0.8s ease-in'
  };

  const headerTitleStyle = {
    fontSize: '2.8rem',
    color: '#1a4a5a',
    marginBottom: '10px',
    fontWeight: '700',
    margin: '0 0 15px 0',
    letterSpacing: '-0.5px'
  };

  const headerSubtitleStyle = {
    fontSize: '1.05rem',
    color: '#306873',
    marginTop: '15px',
    fontWeight: '400'
  };

  const contentStyle = {
    backgroundColor: '#fafafa',
    padding: '50px 50px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e8f0f2'
  };

  const sectionStyle = {
    marginBottom: '45px',
    paddingBottom: '35px',
    borderBottom: '1px solid #e5e5e5'
  };

  const lastSectionStyle = {
    ...sectionStyle,
    borderBottom: 'none',
    marginBottom: '0',
    paddingBottom: '0'
  };

  const h2Style = {
    fontSize: '1.6rem',
    color: '#1a4a5a',
    marginBottom: '20px',
    marginTop: '0',
    fontWeight: '700',
    lineHeight: '1.4',
    paddingBottom: '10px',
    borderBottom: '2px solid #52A7B0'
  };

  const h3Style = {
    fontSize: '1.25rem',
    color: '#306873',
    marginTop: '28px',
    marginBottom: '18px',
    fontWeight: '700',
    letterSpacing: '0.3px'
  };

  const pStyle = {
    fontSize: '0.95rem',
    color: '#444',
    marginBottom: '18px',
    lineHeight: '1.9'
  };

  const ulStyle = {
    margin: '20px 0',
    paddingLeft: '28px',
    listStyle: 'none'
  };

  const liStyle = {
    marginBottom: '16px',
    paddingLeft: '24px',
    position: 'relative',
    color: '#444',
    fontSize: '0.95rem',
    lineHeight: '1.8',
    transition: 'transform 0.2s ease'
  };

  const strongStyle = {
    color: '#1a4a5a',
    fontWeight: '600'
  };

  const contactBoxStyle = {
    backgroundImage: 'linear-gradient(135deg, #e8f4f6 0%, #f0f9fb 100%)',
    padding: '30px',
    borderLeft: '6px solid #52A7B0',
    borderRadius: '8px',
    margin: '30px 0',
    boxShadow: '0 2px 8px rgba(82, 167, 176, 0.1)'
  };

  const linkStyle = {
    color: '#52A7B0',
    textDecoration: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderBottom: '2px solid transparent'
  };

  const SupportSection = ({ title, children }) => (
    <section style={sectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );

  const LastSupportSection = ({ title, children }) => (
    <section style={lastSectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );

  const ListItem = ({ children }) => (
    <li style={liStyle}>
      <span style={{position: 'absolute', left: '0', color: '#52A7B0', fontWeight: 'bold'}}>→</span>
      {children}
    </li>
  );

  return (
    <>
      <Helmet>
        <title>Support - WorkdeskVA Remote Staffing Solutions</title>
        <meta name="description" content="Get support and help with WorkdeskVA remote staffing services. Find answers to common questions and contact our team." />
      </Helmet>
      <div style={pageWrapperStyle}>
        <div style={containerStyle}>
          <div style={headerStyle}>
            <h1 style={headerTitleStyle}>Support & Help Center</h1>
            <p style={headerSubtitleStyle}>
              We're here to help. Find answers to your questions and get support from our team.
            </p>
          </div>

          <div style={contentStyle}>
            <SupportSection title="1. Getting Started">
              <p style={{...pStyle, fontSize: '1.05rem', color: '#333', fontWeight: '500'}}>
                Welcome to <span style={strongStyle}>WorkdeskVA</span>! This guide will help you get started with our remote staffing services.
              </p>
              <h3 style={h3Style}>How to Hire Remote Talent</h3>
              <ul style={ulStyle}>
                <ListItem>Visit our services page to explore available talent categories</ListItem>
                <ListItem>Select the type of remote professional you need</ListItem>
                <ListItem>Fill out your requirements and project details</ListItem>
                <ListItem>Our team will match you with qualified candidates</ListItem>
                <ListItem>Review profiles and conduct interviews</ListItem>
                <ListItem>Onboard your selected team member</ListItem>
              </ul>
            </SupportSection>

            <SupportSection title="2. Frequently Asked Questions">
              <h3 style={h3Style}>General Questions</h3>
              <p style={pStyle}>
                <span style={strongStyle}>Q: What services does WorkdeskVA offer?</span><br />
                A: We provide remote staffing solutions including virtual assistants, software engineers, digital marketers, customer support specialists, and more across various industries.
              </p>
              <p style={pStyle}>
                <span style={strongStyle}>Q: How long does it take to hire?</span><br />
                A: Typically, we can match you with qualified candidates within 3-5 business days, depending on your specific requirements.
              </p>
              <p style={pStyle}>
                <span style={strongStyle}>Q: What are your pricing models?</span><br />
                A: We offer flexible pricing based on role, experience level, and project requirements. Check our pricing page or contact our team for a custom quote.
              </p>

              <h3 style={h3Style}>Payment & Billing</h3>
              <p style={pStyle}>
                <span style={strongStyle}>Q: What payment methods do you accept?</span><br />
                A: We accept credit cards, wire transfers, and bank transfers. All transactions are secure and encrypted.
              </p>
              <p style={pStyle}>
                <span style={strongStyle}>Q: Is there a cancellation policy?</span><br />
                A: Yes, we offer flexible terms with options to adjust or cancel engagements. Contact our support team for details.
              </p>

              <h3 style={h3Style}>Team Management</h3>
              <p style={pStyle}>
                <span style={strongStyle}>Q: Can I adjust team size during a project?</span><br />
                A: Absolutely! We can scale your team up or down based on your changing needs.
              </p>
              <p style={pStyle}>
                <span style={strongStyle}>Q: What about time zone compatibility?</span><br />
                A: We have talent across all time zones and will match candidates suitable for your schedule.
              </p>
            </SupportSection>

            <SupportSection title="3. Account Management">
              <p style={pStyle}>Learn how to manage your WorkdeskVA account and profile.</p>
              <h3 style={h3Style}>Account Settings</h3>
              <ul style={ulStyle}>
                <ListItem>Update your profile information in the account settings</ListItem>
                <ListItem>Manage payment methods and billing information</ListItem>
                <ListItem>Set notification preferences and communication options</ListItem>
                <ListItem>View your hiring history and team details</ListItem>
                <ListItem>Download invoices and contracts</ListItem>
              </ul>

              <h3 style={h3Style}>Security & Privacy</h3>
              <ul style={ulStyle}>
                <ListItem>Enable two-factor authentication for enhanced security</ListItem>
                <ListItem>Change your password regularly</ListItem>
                <ListItem>Review our privacy policy for data protection details</ListItem>
                <ListItem>Control what information you share with team members</ListItem>
              </ul>
            </SupportSection>

            <SupportSection title="4. Team & Talent">
              <h3 style={h3Style}>Finding the Right Talent</h3>
              <ul style={ulStyle}>
                <ListItem>Browse detailed candidate profiles with work samples</ListItem>
                <ListItem>Review portfolio and previous project experience</ListItem>
                <ListItem>Check ratings and testimonials from previous clients</ListItem>
                <ListItem>Conduct interviews through our platform</ListItem>
                <ListItem>Request additional vetting or background checks if needed</ListItem>
              </ul>

              <h3 style={h3Style}>Working With Your Team</h3>
              <ul style={ulStyle}>
                <ListItem>Communicate through our integrated messaging system</ListItem>
                <ListItem>Track time and project progress on the dashboard</ListItem>
                <ListItem>Provide feedback and performance reviews</ListItem>
                <ListItem>Access documentation and deliverables</ListItem>
                <ListItem>Schedule regular check-ins and meetings</ListItem>
              </ul>
            </SupportSection>

            <SupportSection title="5. Technical Support">
              <p style={pStyle}>Having technical issues? We're here to help!</p>
              <h3 style={h3Style}>Common Technical Issues</h3>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Login Problems:</span> Clear your browser cache or try a different browser. Reset your password if needed.</ListItem>
                <ListItem><span style={strongStyle}>Payment Issues:</span> Ensure your payment method is valid and has sufficient funds. Contact your bank if needed.</ListItem>
                <ListItem><span style={strongStyle}>Messaging Not Working:</span> Refresh the page and check your internet connection.</ListItem>
                <ListItem><span style={strongStyle}>File Upload Issues:</span> Check file size (max 50MB) and format compatibility.</ListItem>
                <ListItem><span style={strongStyle}>Video Call Problems:</span> Enable camera/microphone permissions and test your equipment.</ListItem>
              </ul>
            </SupportSection>

            <SupportSection title="6. Best Practices">
              <h3 style={h3Style}>For Optimal Results</h3>
              <ul style={ulStyle}>
                <ListItem>Provide clear, detailed project requirements and expectations</ListItem>
                <ListItem>Communicate regularly with your remote team members</ListItem>
                <ListItem>Use our project management tools to track progress</ListItem>
                <ListItem>Provide constructive feedback to help improve performance</ListItem>
                <ListItem>Document processes and procedures for consistency</ListItem>
                <ListItem>Schedule regular video calls for better connection</ListItem>
                <ListItem>Be flexible with time zones and cultural differences</ListItem>
              </ul>

              <h3 style={h3Style}>Communication Tips</h3>
              <ul style={ulStyle}>
                <ListItem>Use written documentation for important decisions</ListItem>
                <ListItem>Be clear about deadlines and deliverables</ListItem>
                <ListItem>Provide context and background information</ListItem>
                <ListItem>Ask clarifying questions when needed</ListItem>
                <ListItem>Give timely feedback and performance reviews</ListItem>
              </ul>
            </SupportSection>

            <SupportSection title="7. Billing & Invoices">
              <p style={pStyle}>Understand our billing process and manage your invoices.</p>
              <h3 style={h3Style}>Payment Terms</h3>
              <ul style={ulStyle}>
                <ListItem>Monthly billing cycles begin on your service start date</ListItem>
                <ListItem>Invoices are issued and payable at the beginning of each month</ListItem>
                <ListItem>Late payments may result in service suspension</ListItem>
                <ListItem>Discounts available for annual prepayment</ListItem>
              </ul>

              <h3 style={h3Style}>Invoice Management</h3>
              <ul style={ulStyle}>
                <ListItem>Download invoices from your account dashboard</ListItem>
                <ListItem>View detailed breakdowns of services and hours</ListItem>
                <ListItem>Export invoices for accounting purposes</ListItem>
                <ListItem>Request payment receipts and tax documentation</ListItem>
              </ul>
            </SupportSection>

            <SupportSection title="8. Onboarding Your Team">
              <h3 style={h3Style}>Onboarding Checklist</h3>
              <ul style={ulStyle}>
                <ListItem>Confirm candidate availability and start date</ListItem>
                <ListItem>Share company information and culture overview</ListItem>
                <ListItem>Provide tools, software, and access credentials</ListItem>
                <ListItem>Schedule orientation and training sessions</ListItem>
                <ListItem>Assign an internal mentor or point of contact</ListItem>
                <ListItem>Set clear goals and performance expectations</ListItem>
                <ListItem>Conduct first week check-in meetings</ListItem>
              </ul>

              <h3 style={h3Style}>Setting Expectations</h3>
              <ul style={ulStyle}>
                <ListItem>Define working hours and availability requirements</ListItem>
                <ListItem>Explain communication protocols and preferred tools</ListItem>
                <ListItem>Share project timelines and milestones</ListItem>
                <ListItem>Establish performance metrics and KPIs</ListItem>
                <ListItem>Clarify NDA and confidentiality agreements</ListItem>
              </ul>
            </SupportSection>

            <LastSupportSection title="9. Contact & Support Channels">
              <p style={pStyle}>Can't find what you're looking for? Reach out to our support team!</p>
              <div style={contactBoxStyle}>
                <p style={{...pStyle, margin: '0'}}>
                  <span style={strongStyle}>WorkdeskVA Support Team</span><br />
                  Email: <a href="mailto:support@workdeskva.com" style={linkStyle}>support@workdeskva.com</a><br />
                  Phone: <a href="tel:+1234567890" style={linkStyle}>+1 (234) 567-890</a><br />
                  Live Chat: Available on our website during business hours<br />
                  Response Time: Within 24 business hours
                </p>
              </div>

              <h3 style={h3Style}>Support Hours</h3>
              <p style={pStyle}>
                Our support team is available Monday through Friday, 9:00 AM to 6:00 PM (EST). We typically respond to all inquiries within 24 business hours.
              </p>

              <h3 style={h3Style}>Additional Resources</h3>
              <ul style={ulStyle}>
                <ListItem>Visit our <a href="/blog" style={linkStyle}>Blog</a> for industry insights and tips</ListItem>
                <ListItem>Check our <a href="/privacy-policy" style={linkStyle}>Privacy Policy</a> for data protection information</ListItem>
                <ListItem>Review our Terms of Service for service agreement details</ListItem>
                <ListItem>Access knowledge base articles for self-service help</ListItem>
              </ul>
            </LastSupportSection>
          </div>
        </div>
      </div>
    </>
  );
};

export default Support;

